import { AbschnittDisplayModel, AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode, AenderungsantragDisplayModel, AnfrageDisplayModel, GlobalEnumerationsAceAnzeigeunterabschnitte, ValidationFailure } from '@/api/model-index';
import { AnfrageService } from '@/api/service-index';
import { Constants } from '@/constants';
import { DetailSidepaneComponent } from '@/core/components/detail-sidepane/detail-sidepane.component';
import { AnfrageLiveSharingComponent } from '@/core/features/anfrage/components/anfrage-live-sharing/anfrage-live-sharing.component';
import { NavigationItem } from '@/core/models/navigation-item.model';
import { AnfrageTransformerService } from '@/core/services/anfrage-transformer.service';
import { ComponentService } from '@/core/services/component.service';
import { DraggableSidebarService } from '@/core/services/draggable-sidebar.service';
import { FormularService } from '@/core/services/formular.service';
import { I18nService } from '@/core/services/i18nService';
import { NavigationService } from '@/core/services/navigation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { StateService } from '@/core/services/state.service';
import { Component, inject, OnDestroy, OnInit, Type } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
    selector: 'aenderungsantrag-detail',
    templateUrl: './aenderungsantrag-detail.component.html',
    styleUrls: ['./aenderungsantrag-detail.component.scss'],
    standalone: false
})
export class AenderungsantragDetailComponent implements OnInit, OnDestroy {
  protected _navItemList: NavigationItem[] | null;
  protected draggableSidebar = inject(DraggableSidebarService);
  protected componentService = inject(ComponentService);
  protected subscriptions: Subscription[] = [];

  public anfrage: AnfrageDisplayModel;
  public area: { [key: string]: ValidationFailure[] };

  public get anfrageLiveSharingComponent(): Type<any> {
    return this.componentService.get(AnfrageLiveSharingComponent);
  }

  public get detailSidepaneComponent(): Type<DetailSidepaneComponent> {
    return this.componentService.get(DetailSidepaneComponent);
  }

  public get showProzessNavigation() {
    return this.stateService.showProzessNavigation;
  }

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected formularService: FormularService,
    protected stateService: StateService,
    protected portalContextService: PortalContextService,
    protected languageservice: I18nService,
    protected anfrageService: AnfrageService,
    protected anfrageTransformerService: AnfrageTransformerService,
    protected navigationService: NavigationService) {

    this.anfrage = this.portalContextService.CurrentAnfrage;
    this.area = this.route.snapshot.data.area;

    this.stateService.ValidationErrors = this.area;

    this.subscriptions.push(this.languageservice.languageChanged$.subscribe(() => {
      this.ngOnInit();
    }));

    this.subscriptions.push(this.portalContextService.CurrentAenderungsantragChanged.subscribe((aenderungsantrag) => {
      this.fetchValidationErrors(aenderungsantrag);
    }));

    this.fetchValidationErrors(this.portalContextService.CurrentAenderungsantrag);
  }

  public shouldShowSubsectionsInNavigation(parentAbschnitt: AbschnittDisplayModel): boolean {
    // Wenn Unterabschnitte als Tabs angezeigt werden sollen, nicht in der Navigation anzeigen
    return parentAbschnitt.anzeigeUnterabschnitte !== GlobalEnumerationsAceAnzeigeunterabschnitte.uberTabs;
  }

  public hasNephews(navItemList: NavigationItem[] | null): boolean {
    if (!navItemList) return false;

    return navItemList.some(item => this.shouldShowSubsectionsInNavigation(item.abschnitt) && item.childAbschnitte && item.childAbschnitte.length > 0);
  }

  protected fetchValidationErrors(aenderungsantrag: AenderungsantragDisplayModel | null) {
    this.stateService.ValidationErrors = null;
    if (this.portalContextService?.CurrentAnfrage?.id && aenderungsantrag?.id && !this.portalContextService.isAnfrageDisabled()) {
      this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.portalContextService.CurrentAnfrage.id, aenderungsantragId: aenderungsantrag?.id }).subscribe(data => {
        this.stateService.ValidationErrors = data;
      });
    }
  }

  public get navItemList() {
    return this._navItemList;
  }

  ngOnInit(): void {
    if (this.portalContextService.CurrentAnfrage.phase?.abschnitte) {
      this.setNavItems(this.portalContextService.CurrentAnfrage.phase.abschnitte);
    }
    this.subscriptions.push(this.portalContextService.AnfrageChanged.subscribe((anfrage: AnfrageDisplayModel) => {
      this.setNavItems(anfrage?.phase?.abschnitte ?? []);
    }));
    this.subscriptions.push(this.languageservice.languageChanged$.subscribe(async () => {
      const transformedAnfrage = await this.anfrageTransformerService.transform(this.portalContextService.CurrentAnfrage);
      this.portalContextService.CurrentAnfrage = transformedAnfrage;
    }));
  }

  protected setNavItems(abschnitte: AbschnittDisplayModel[]) {
    this._navItemList = this.navigationService.getNavItems(abschnitte);
    this._navItemList?.forEach(navItem => {
      navItem.children = this.navigationService.getNavItems(navItem.childAbschnitte);
    });
  }

  public iconReportStatus(abschnittId: string | undefined): string {
    let foundAenderungsantrag = this.portalContextService.Aenderungsantraege?.find(aenderungsantrag => aenderungsantrag.id == abschnittId);
    switch (foundAenderungsantrag?.status) {
      case AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Entwurf:
        return "pencil";
      case AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Erfasst:
        return "eye";
      case AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.InPrufung:
        return "user-round-search";
      case AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Entscheidung:
        return "scale";
      case AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Genehmigt:
        return "check";
      case AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode.Abgelehnt:
        return "x";
    }
    return "file-question-mark";
  }

  public iconReportStatusTitle(abschnittId: string | undefined): string {
    let foundAenderungsantrag = this.portalContextService.Aenderungsantraege?.find(aenderungsantrag => aenderungsantrag.id == abschnittId);

    if (foundAenderungsantrag) {
      return this.languageservice.getEnumValue(AceAenderungsantragAceAenderungsantragEnumsAceAenderungsantragStatuscode, foundAenderungsantrag.status)?.Description || "";
    }
    return "";
  }

  public isParentOpen(parentAbschnitt: AbschnittDisplayModel) {
    let abschnittId: string | null = <string>this.route.firstChild?.snapshot.params['abschnittid'];
    let isOpen = false;

    // If parent is active and has children
    if (parentAbschnitt.abschnitte && this.isCurrentRoute(parentAbschnitt)) {
      isOpen = true;
    }

    // If a child was found in parent, then show group
    let childAbschnitt = parentAbschnitt.abschnitte?.find(a => a.id == abschnittId);
    if (childAbschnitt) {
      isOpen = true;
    }

    return isOpen;
  }

  public isCurrentRoute(parentAbschnitt: AbschnittDisplayModel, childAbschnitt?: AbschnittDisplayModel) {
    let abschnittId: string | null = <string>this.route.firstChild?.snapshot.params['abschnittid'];

    if (!abschnittId) return false;

    // Parent is active
    if (parentAbschnitt.id == abschnittId && !childAbschnitt) {
      return true;
    }

    if (childAbschnitt && childAbschnitt.id == abschnittId) {
      return true;
    }

    // Wenn der Parent Tabs hat und der aktuelle Abschnitt ein Unterabschnitt ist, 
    // zeige den Parent als aktiv an
    if (!childAbschnitt && 
        parentAbschnitt.anzeigeUnterabschnitte === GlobalEnumerationsAceAnzeigeunterabschnitte.uberTabs &&
        parentAbschnitt.abschnitte?.some(sub => sub.id === abschnittId)) {
      return true;
    }

    return false;
  }

  onAppNavigationItemClick(): void {
    if (window.innerWidth < 1024) {
      this.stateService.showProzessNavigation = false;
    }
  }

  protected nullReference: AnfrageDisplayModel;
  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.portalContextService.CurrentAnfrage = this.nullReference;
    this.portalContextService.AenderungsantragsPhase = null;
    this.portalContextService.CurrentAenderungsantrag = null;
    this.portalContextService.Aenderungsantraege = null;
    this.stateService.ValidationErrors = null;
  }
}
